/* eslint-disable react/prop-types */
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiAlertCircle,
  FiCalendar,
  FiCamera,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiMail,
  FiRefreshCw,
  FiShoppingBag,
  FiTrash2,
  FiUpload,
} from 'react-icons/fi';
import { GiPeanut } from 'react-icons/gi';
import { AdminContext } from '../../Context/AdminContext';
import { apiRequest } from '../../Services/api';
import { formatEasternDate } from '../../Utils/dateTime';
import './ProfilePage.css';

const DEFAULT_AVATAR = '/images/auntjacks-logo.png';
const INITIAL_ORDER_LIMIT = 3;
const EXPANDED_ORDER_LIMIT = 25;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  ready: 'Ready for pickup',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function formatPickupDate(value) {
  if (!value) return 'Date unavailable';
  return formatEasternDate(value, { month: 'short', day: 'numeric', year: 'numeric' });
}

function OrderRow({ order, expanded, onToggle }) {
  const detailsId = `order-${order.id}-details`;
  return (
    <article className={`account-order ${expanded ? 'is-expanded' : ''}`}>
      <div className="account-order-summary">
        <div className="order-number">
          <span className="order-mobile-label">Order</span>
          <strong>#{order.id}</strong>
        </div>
        <div className="order-date">
          <FiCalendar aria-hidden="true" />
          <span>{formatPickupDate(order.pickupDate)}</span>
        </div>
        <div>
          <span className={`order-status status-${order.status}`}>
            {STATUS_LABELS[order.status] || order.status}
          </span>
        </div>
        <strong className="order-total">${order.totalAmount}</strong>
        <button
          type="button"
          className="order-details-button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={detailsId}
        >
          <span>{expanded ? 'Hide details' : 'View details'}</span>
          {expanded ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
        </button>
      </div>

      {expanded ? (
        <div className="order-line-items" id={detailsId}>
          {order.items.length > 0 ? order.items.map((item) => (
            <div className="order-line-item" key={item.id}>
              <div>
                <strong>{item.productName}</strong>
                <span>{item.sizeLabel} · Qty {item.quantity}</span>
              </div>
              <span>${item.lineTotal}</span>
            </div>
          )) : <p>Item details are unavailable for this order.</p>}
        </div>
      ) : null}
    </article>
  );
}

function AccountSkeleton() {
  return (
    <main className="account-page account-loading" aria-busy="true" aria-label="Loading your account">
      <div className="account-shell">
        <div className="skeleton skeleton-title" />
        <div className="skeleton skeleton-summary" />
        <div className="skeleton skeleton-content" />
      </div>
    </main>
  );
}

function ProfilePage() {
  const { user, authLoading, syncUser } = useContext(AdminContext);
  const userId = user?.id;
  const location = useLocation();
  const navigate = useNavigate();
  const firstFieldRef = useRef(null);
  const fileInputRef = useRef(null);
  const processedEmailToken = useRef('');

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const [formData, setFormData] = useState({ displayName: '', phone: '', bio: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [profileSubmitting, setProfileSubmitting] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [avatarSubmitting, setAvatarSubmitting] = useState(false);

  const [pendingEmail, setPendingEmail] = useState(null);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ newEmail: '', currentPassword: '' });
  const [emailErrors, setEmailErrors] = useState({});
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailConfirming, setEmailConfirming] = useState(false);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  const [pagination, setPagination] = useState({ limit: INITIAL_ORDER_LIMIT, offset: 0, total: 0, hasMore: false });
  const [showingAllOrders, setShowingAllOrders] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState(() => new Set());

  useEffect(() => {
    const previousTitle = document.title;
    const robots = document.querySelector('meta[name="robots"]');
    const previousRobots = robots?.getAttribute('content') ?? null;
    document.title = "My Account | Aunt Jack's Peanut Shack";
    robots?.setAttribute('content', 'noindex,nofollow');

    return () => {
      document.title = previousTitle;
      if (robots && previousRobots !== null) robots.setAttribute('content', previousRobots);
    };
  }, []);

  useEffect(() => {
    if (!imagePreview) return undefined;
    return () => URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  useEffect(() => {
    if (authLoading || !userId) return undefined;
    let active = true;
    setProfileLoading(true);
    setOrdersLoading(true);

    const profileRequest = apiRequest('/profile/index.php');
    const emailRequest = apiRequest('/profile/email-change.php');
    const orderRequest = apiRequest(`/orders/index.php?limit=${INITIAL_ORDER_LIMIT}&offset=0`);

    Promise.allSettled([profileRequest, emailRequest, orderRequest]).then(([profileResult, emailResult, orderResult]) => {
      if (!active) return;

      if (profileResult.status === 'fulfilled') {
        const nextProfile = profileResult.value.profile;
        setProfile(nextProfile);
        setFormData({
          displayName: nextProfile.displayName || '',
          phone: nextProfile.phone || '',
          bio: nextProfile.bio || '',
        });
        syncUser(nextProfile);
        setProfileError('');
      } else {
        setProfileError(profileResult.reason.message || 'Unable to load your profile.');
      }
      setProfileLoading(false);

      if (emailResult.status === 'fulfilled') {
        setPendingEmail(emailResult.value.request);
      }

      if (orderResult.status === 'fulfilled') {
        setOrders(orderResult.value.orders);
        setPagination(orderResult.value.pagination);
        setOrdersError('');
      } else {
        setOrdersError(orderResult.reason.message || 'Unable to load your orders.');
      }
      setOrdersLoading(false);
    });

    return () => { active = false; };
  }, [authLoading, userId, syncUser]);

  useEffect(() => {
    if (authLoading || !user || !location.hash.startsWith('#emailChangeToken=')) return;

    const token = new URLSearchParams(location.hash.slice(1)).get('emailChangeToken') || '';
    if (!token || processedEmailToken.current === token) return;
    processedEmailToken.current = token;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true });
    setEmailConfirming(true);

    apiRequest('/profile/email-change.php', {
      method: 'PATCH',
      body: JSON.stringify({ token }),
    })
      .then((data) => {
        setProfile(data.profile);
        setFormData({
          displayName: data.profile.displayName || '',
          phone: data.profile.phone || '',
          bio: data.profile.bio || '',
        });
        setPendingEmail(null);
        syncUser(data.profile);
        toast.success('Your email address has been verified and updated.');
      })
      .catch((error) => toast.error(error.message || 'Unable to verify this email address.'))
      .finally(() => setEmailConfirming(false));
  }, [authLoading, user, location.hash, location.pathname, location.search, navigate, syncUser]);

  const loadOrders = useCallback(async ({ limit, offset = 0, append = false }) => {
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const data = await apiRequest(`/orders/index.php?limit=${limit}&offset=${offset}`);
      setOrders((current) => append ? [...current, ...data.orders] : data.orders);
      setPagination(data.pagination);
    } catch (error) {
      setOrdersError(error.message || 'Unable to load your orders.');
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setFieldErrors((current) => ({ ...current, [name]: '' }));
  };

  const validateProfile = () => {
    const errors = {};
    const phonePattern = /^[0-9+() .-]{7,30}$/;
    if (!formData.displayName.trim() || formData.displayName.trim().length > 100) {
      errors.displayName = 'Enter a name up to 100 characters.';
    }
    if (!phonePattern.test(formData.phone.trim())) {
      errors.phone = 'Enter a valid phone number.';
    }
    if (formData.bio.trim().length > 500) {
      errors.bio = 'Bio must be 500 characters or fewer.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    if (!validateProfile()) return;
    setProfileSubmitting(true);
    try {
      const data = await apiRequest('/profile/index.php', {
        method: 'PATCH',
        body: JSON.stringify({
          displayName: formData.displayName.trim(),
          phone: formData.phone.trim(),
          bio: formData.bio.trim(),
        }),
      });
      setProfile(data.profile);
      syncUser(data.profile);
      setFieldErrors({});
      toast.success('Profile saved.');
    } catch (error) {
      setFieldErrors(error.errors || {});
      toast.error(error.message || 'Unable to save your profile.');
    } finally {
      setProfileSubmitting(false);
    }
  };

  const handleImageSelection = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.has(file.type) || file.size > 5 * 1024 * 1024) {
      toast.error('Choose a JPEG, PNG, or WebP image up to 5 MB.');
      event.target.value = '';
      return;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearSelectedImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setSelectedImage(null);
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAvatarUpload = async () => {
    if (!selectedImage) return;
    setAvatarSubmitting(true);
    const body = new FormData();
    body.append('image', selectedImage);
    try {
      const data = await apiRequest('/profile/photo.php', { method: 'POST', body });
      setProfile(data.profile);
      syncUser(data.profile);
      clearSelectedImage();
      toast.success('Profile photo updated.');
    } catch (error) {
      toast.error(error.message || 'Unable to upload your photo.');
    } finally {
      setAvatarSubmitting(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (!window.confirm('Remove your current profile photo?')) return;
    setAvatarSubmitting(true);
    try {
      const data = await apiRequest('/profile/photo.php', { method: 'DELETE' });
      setProfile(data.profile);
      syncUser(data.profile);
      clearSelectedImage();
      toast.success('Profile photo removed.');
    } catch (error) {
      toast.error(error.message || 'Unable to remove your photo.');
    } finally {
      setAvatarSubmitting(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setEmailErrors({});
    setEmailSubmitting(true);
    try {
      const data = await apiRequest('/profile/email-change.php', {
        method: 'POST',
        body: JSON.stringify({
          newEmail: emailForm.newEmail.trim(),
          currentPassword: emailForm.currentPassword,
        }),
      });
      setPendingEmail(data.request);
      setEmailForm({ newEmail: '', currentPassword: '' });
      setEmailFormOpen(false);
      toast.success(data.message || 'Verification email sent.');
    } catch (error) {
      setEmailErrors(error.errors || {});
      toast.error(error.message || 'Unable to request an email change.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  const toggleOrder = (orderId) => {
    setExpandedOrders((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  if (authLoading) return <AccountSkeleton />;
  if (!user) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/auth" replace state={{ returnTo }} />;
  }
  if (profileLoading) return <AccountSkeleton />;

  return (
    <main className="account-page">
      <div className="account-shell">
        <h1>My Account</h1>

        {profileError ? (
          <div className="account-error account-profile-error" role="alert">
            <FiAlertCircle aria-hidden="true" />
            <div>
              <strong>We couldn&apos;t load your account.</strong>
              <p>{profileError}</p>
            </div>
          </div>
        ) : (
          <>
            <section className="account-intro" aria-label="Account summary">
              <div className="profile-summary">
                <img
                  className="account-avatar account-avatar-large"
                  src={imagePreview || profile?.photoURL || DEFAULT_AVATAR}
                  alt={`${profile?.displayName || 'Customer'} profile`}
                />
                <div>
                  <h2>{profile?.displayName}</h2>
                  <p>{profile?.email}</p>
                  <button
                    type="button"
                    className="text-action"
                    onClick={() => firstFieldRef.current?.focus()}
                  >
                    <FiEdit2 aria-hidden="true" /> Edit profile
                  </button>
                </div>
              </div>

              <div className="order-cta-panel">
                <div className="peanut-mark" aria-hidden="true"><GiPeanut /></div>
                <div>
                  <strong>Ready for more Southern flavor?</strong>
                  <p>Place a fresh order for pickup at your next event.</p>
                </div>
                <Link className="account-primary-button" to="/shop">
                  <FiShoppingBag aria-hidden="true" /> Order peanuts
                </Link>
              </div>
            </section>

            <div className="account-content-grid">
              <section className="profile-contact-section" aria-labelledby="profile-contact-heading">
                <div className="account-section-heading">
                  <h2 id="profile-contact-heading">Profile &amp; Contact</h2>
                  <p>Keep your information up to date.</p>
                </div>

                <div className="avatar-editor">
                  <span className="field-label">Profile photo</span>
                  <img
                    className="account-avatar account-avatar-small"
                    src={imagePreview || profile?.photoURL || DEFAULT_AVATAR}
                    alt="Profile preview"
                  />
                  <div className="avatar-actions">
                    <input
                      ref={fileInputRef}
                      className="visually-hidden-file"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageSelection}
                    />
                    <button
                      type="button"
                      className="account-outline-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={avatarSubmitting}
                    >
                      <FiCamera aria-hidden="true" /> Change photo
                    </button>
                    {selectedImage ? (
                      <>
                        <button type="button" className="account-primary-button compact" onClick={handleAvatarUpload} disabled={avatarSubmitting}>
                          <FiUpload aria-hidden="true" /> {avatarSubmitting ? 'Uploading…' : 'Upload photo'}
                        </button>
                        <button type="button" className="account-quiet-button" onClick={clearSelectedImage} disabled={avatarSubmitting}>Cancel</button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="account-quiet-button"
                        onClick={handleAvatarRemove}
                        disabled={avatarSubmitting || profile?.photoURL === DEFAULT_AVATAR}
                      >
                        <FiTrash2 aria-hidden="true" /> Remove
                      </button>
                    )}
                  </div>
                </div>

                <form className="account-form" onSubmit={handleProfileSubmit} noValidate>
                  <div className="account-field">
                    <label htmlFor="displayName">Full name</label>
                    <input
                      ref={firstFieldRef}
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleProfileChange}
                      maxLength={100}
                      aria-invalid={Boolean(fieldErrors.displayName)}
                      aria-describedby={fieldErrors.displayName ? 'displayName-error' : undefined}
                    />
                    {fieldErrors.displayName ? <span id="displayName-error" className="field-error">{fieldErrors.displayName}</span> : null}
                  </div>

                  <div className="account-field">
                    <label htmlFor="profileEmail">Email</label>
                    <div className="email-readonly-row">
                      <input id="profileEmail" type="email" value={profile?.email || ''} readOnly />
                      <button type="button" className="text-action" onClick={() => setEmailFormOpen((open) => !open)}>
                        <FiMail aria-hidden="true" /> Change
                      </button>
                    </div>
                  </div>

                  {pendingEmail ? (
                    <div className="pending-email-note" role="status">
                      <FiCheckCircle aria-hidden="true" />
                      <span>Verification sent to <strong>{pendingEmail.newEmail}</strong>.</span>
                    </div>
                  ) : null}

                  <div className="account-field">
                    <label htmlFor="phone">Phone</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleProfileChange}
                      maxLength={30}
                      aria-invalid={Boolean(fieldErrors.phone)}
                      aria-describedby={fieldErrors.phone ? 'phone-error' : undefined}
                    />
                    {fieldErrors.phone ? <span id="phone-error" className="field-error">{fieldErrors.phone}</span> : null}
                  </div>

                  <div className="account-field">
                    <div className="label-with-count">
                      <label htmlFor="bio">Bio (optional)</label>
                      <span>{formData.bio.length}/500</span>
                    </div>
                    <textarea
                      id="bio"
                      name="bio"
                      rows={4}
                      value={formData.bio}
                      onChange={handleProfileChange}
                      maxLength={500}
                      aria-invalid={Boolean(fieldErrors.bio)}
                      aria-describedby={fieldErrors.bio ? 'bio-error' : undefined}
                    />
                    {fieldErrors.bio ? <span id="bio-error" className="field-error">{fieldErrors.bio}</span> : null}
                  </div>

                  <button type="submit" className="account-primary-button save-profile-button" disabled={profileSubmitting}>
                    {profileSubmitting ? 'Saving…' : 'Save changes'}
                  </button>
                </form>

                {emailFormOpen ? (
                  <form className="email-change-form" onSubmit={handleEmailSubmit} noValidate>
                    <div className="email-change-heading">
                      <h3>Change email address</h3>
                      <p>We&apos;ll keep your current email active until you confirm the new one.</p>
                    </div>
                    <div className="account-field">
                      <label htmlFor="newEmail">New email</label>
                      <input
                        id="newEmail"
                        type="email"
                        autoComplete="email"
                        value={emailForm.newEmail}
                        onChange={(event) => setEmailForm((current) => ({ ...current, newEmail: event.target.value }))}
                        aria-invalid={Boolean(emailErrors.newEmail)}
                      />
                      {emailErrors.newEmail ? <span className="field-error">{emailErrors.newEmail}</span> : null}
                    </div>
                    <div className="account-field">
                      <label htmlFor="currentPassword">Current password</label>
                      <input
                        id="currentPassword"
                        type="password"
                        autoComplete="current-password"
                        value={emailForm.currentPassword}
                        onChange={(event) => setEmailForm((current) => ({ ...current, currentPassword: event.target.value }))}
                        aria-invalid={Boolean(emailErrors.currentPassword)}
                      />
                      {emailErrors.currentPassword ? <span className="field-error">{emailErrors.currentPassword}</span> : null}
                    </div>
                    <div className="email-form-actions">
                      <button type="submit" className="account-primary-button compact" disabled={emailSubmitting}>
                        {emailSubmitting ? 'Sending…' : 'Send verification'}
                      </button>
                      <button type="button" className="account-quiet-button" onClick={() => setEmailFormOpen(false)}>Cancel</button>
                    </div>
                  </form>
                ) : null}
              </section>

              <section className="order-history-section" aria-labelledby="order-history-heading">
                <div className="account-section-heading">
                  <h2 id="order-history-heading">Order History</h2>
                  <p>Here are your recent orders.</p>
                </div>

                {emailConfirming ? (
                  <div className="email-confirming" role="status">
                    <FiRefreshCw className="spin-icon" aria-hidden="true" /> Confirming your email address…
                  </div>
                ) : null}

                {ordersError ? (
                  <div className="account-error" role="alert">
                    <FiAlertCircle aria-hidden="true" />
                    <div>
                      <strong>Orders are temporarily unavailable.</strong>
                      <p>{ordersError}</p>
                      <button type="button" className="account-outline-button" onClick={() => loadOrders({ limit: showingAllOrders ? EXPANDED_ORDER_LIMIT : INITIAL_ORDER_LIMIT })}>
                        <FiRefreshCw aria-hidden="true" /> Try again
                      </button>
                    </div>
                  </div>
                ) : null}

                {!ordersError && orders.length === 0 && !ordersLoading ? (
                  <div className="orders-empty">
                    <FiShoppingBag aria-hidden="true" />
                    <h3>No orders yet</h3>
                    <p>Your pickup orders will appear here.</p>
                    <Link to="/shop" className="account-primary-button">Order peanuts</Link>
                  </div>
                ) : null}

                {orders.length > 0 ? (
                  <div className="orders-list">
                    <div className="orders-table-heading" aria-hidden="true">
                      <span>Order #</span><span>Pickup date</span><span>Status</span><span>Total</span><span />
                    </div>
                    {orders.map((order) => (
                      <OrderRow
                        key={order.id}
                        order={order}
                        expanded={expandedOrders.has(order.id)}
                        onToggle={() => toggleOrder(order.id)}
                      />
                    ))}
                  </div>
                ) : null}

                {ordersLoading ? <div className="orders-loading" role="status">Loading orders…</div> : null}

                {!ordersLoading && !ordersError && orders.length > 0 && !showingAllOrders && pagination.total > INITIAL_ORDER_LIMIT ? (
                  <button
                    type="button"
                    className="account-outline-button view-orders-button"
                    onClick={() => {
                      setShowingAllOrders(true);
                      loadOrders({ limit: EXPANDED_ORDER_LIMIT });
                    }}
                  >
                    View all orders
                  </button>
                ) : null}

                {!ordersLoading && showingAllOrders && pagination.hasMore ? (
                  <button
                    type="button"
                    className="account-outline-button view-orders-button"
                    onClick={() => loadOrders({ limit: EXPANDED_ORDER_LIMIT, offset: orders.length, append: true })}
                  >
                    Load more
                  </button>
                ) : null}
              </section>
            </div>
          </>
        )}
      </div>
    </main>
  );
}

export default ProfilePage;
